import { useEffect, useState } from 'react'
import { useApiCall } from '../../../component/custom-hooks/api-call';
import { ord, pkr, Section } from '.';
import InnerLoader from '../../../component/ui/loader/inner-loader';
import DataNotFound from '../../../component/ui/loader/data-not-found';
import { Repeat, Zap } from 'lucide-react';




const RecurringBill = ({ toast = () => { }, inpStyle, C }) => {
    const selStyle = { ...inpStyle, cursor: "pointer" };
    const apiCall = useApiCall();
    const [isLoading, setIsLoading] = useState(true);


    // Recurring bills actions
    const [recurForm, setRecurForm] = useState({});
    const [genMonth, setGenMonth] = useState("March");
    const [genYear, setGenYear] = useState(2026);
    const [recurModal, setRecurModal] = useState(null); // null | "new" | row id
    const [recurring, setRecurring] = useState([]);
    const [categories, setCategories] = useState([]);
    const [locations, setLocations] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);

    const fetchRecurring = async () => {
        setIsLoading(true);
        try {
            const res = await apiCall({ url: "cash-flow/recurring-bills/list", method: "GET" });
            const mappedData = res.data.map(item => ({
                id: item.id,
                expense: item.expense_name,
                cat: item.category_name,
                loc: item.location_name,
                amt: parseFloat(item.amount),
                dueDay: item.due_day,
                method: item.payment_method,
                is_active: item.is_active
            }));
            setRecurring(mappedData);
            setIsLoading(false);
        } catch (error) {
            toast("Error fetching recurring bills", "error");
            setIsLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await apiCall({ url: "cash-flow/categories/list", method: "GET" });
            setCategories(res.data);
        } catch (error) {
            console.error("Error fetching categories", error);
        }
    };

    const fetchLocations = async () => {
        try {
            const res = await apiCall({ url: "cash-flow/locations/list", method: "GET" });
            setLocations(res.data);
        } catch (error) {
            console.error("Error fetching locations", error);
        }
    };

    const fetchPaymentMethods = async () => {
        try {
            const res = await apiCall({ url: "cash-flow/dropdown", method: "GET" });
            setPaymentMethods(res?.dropdown?.method || []);
        } catch (error) {
            console.error("Error fetching payment methods", error);
        }
    };

    useEffect(() => {
        fetchRecurring();
        fetchCategories();
        fetchLocations();
        fetchPaymentMethods();
    }, []);

    const openNewRecur = () => {
        setRecurForm({ expense: "", cat: "", loc: "", amt: "", dueDay: "1", method: "", is_active: true });
        setRecurModal("new");
    };
    const openEditRecur = (row) => {
        setRecurForm({ ...row, amt: String(row.amt || ""), dueDay: String(row.dueDay || "") });
        setRecurModal(row.id);
    };

    const saveRecur = async () => {
        const row = { ...recurForm, amt: parseFloat(recurForm.amt) || 0, dueDay: recurForm.dueDay ? parseInt(recurForm.dueDay) : null, is_active: true };
        if (recurModal === "new") {
            const category = categories.find(c => c.name === recurForm.cat);
            const location = locations.find(l => l.name === recurForm.loc);
            if (!category || !location) {
                toast("Invalid category or location", "error");
                return;
            }
            try {
                await apiCall({
                    url: "cash-flow/recurring-bills/add",
                    method: "POST",
                    data: {
                        expense_name: recurForm.expense,
                        category_id: category.id,
                        location_id: location.id,
                        amount: row.amt,
                        due_day: row.dueDay,
                        payment_method: recurForm.method,
                        is_active: true
                    }
                });
                toast("Recurring bill added", "success");
                fetchRecurring();
            } catch (error) {
                toast("Error adding recurring bill", "error");
            }
        } else {
            const category = categories.find(c => c.name === recurForm.cat);
            const location = locations.find(l => l.name === recurForm.loc);
            if (!category || !location) {
                toast("Invalid category or location", "error");
                return;
            }
            try {
                await apiCall({
                    url: "cash-flow/recurring-bills/update",
                    method: "POST",
                    data: {
                        id: recurModal,
                        expense_name: recurForm.expense,
                        category_id: category.id,
                        location_id: location.id,
                        amount: row.amt,
                        due_day: row.dueDay,
                        payment_method: recurForm.method,
                        is_active: row?.is_active
                    }
                });
                toast("Recurring bill updated", "success");
                fetchRecurring();
            } catch (error) {
                toast("Error updating recurring bill", "error");
            }
        }
        setRecurModal(null);
    };

    const toggleRecur = async (id) => {
        const newdata = recurring.map(c => c.id === id ? { ...c, is_active: !c.is_active } : c);
        setRecurring(newdata);
        try {
            await apiCall({
                url: `cash-flow/recurring-bills/${id}/update-status`,
                method: "POST",
            });
            toast("Status updated", "success");
        } catch (error) {
            console.error("Error toggling recurring bill active status:", error?.message || error);
            toast("Error updating recurring bill status", "error");
            fetchRecurring();
        }
    };

    const generateBills = async () => {
        try {
            const res = await apiCall({
                url: "cash-flow/bills/generate",
                method: "POST",
                data: {
                    bill_month: genMonth,
                    bill_year: genYear
                }
            });
            const newBills = res?.bills;

            // In wired app: pushes to shared bills state. Standalone: show confirmation.
            if (newBills.length > 0) {
                toast(`Generated ${newBills.length} bills for ${genMonth} ${genYear}`, "success");
            } else {
                toast("No bills generated. Check if there are active recurring entries.", "info");
            }

        } catch (error) {
            console.error("Error generating recurring bills:", error?.message || error);
            toast("Error generating recurring bills", "error");
        }


    };



    return (
        <>
            {/* ── 2. RECURRING BILLS ── */}
            <Section
                id="recurring"
                icon={<Repeat />}
                title="Recurring Bills"
                desc="Bills that generate automatically every month — edit amounts, toggle on/off"
                C={C}
                Btn={
                    <button onClick={openNewRecur}
                        style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", marginTop: 12, borderRadius: 9, border: `1px dashed ${C.border}`, background: "#f9fafb", fontSize: 12, fontWeight: 600, color: C.textSec, cursor: "pointer" }}>
                        + Add Recurring Bill
                    </button>
                }
            >

                {/* Generate bar */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 11, background: "linear-gradient(135deg,#f0f4ff,#faf5ff)", border: "1px solid #c7d2fe", marginBottom: 20 }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.textPri, marginBottom: 2 }}>Generate bills for a month</div>
                        <div style={{ fontSize: 11, color: C.textMut }}>Creates one bill per active recurring entry in the Bills screen</div>
                    </div>
                    <select value={genMonth} onChange={e => setGenMonth(e.target.value)}
                        style={{ ...inpStyle, width: 120 }}>
                        {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map(m => <option key={m}>{m}</option>)}
                    </select>
                    <input type="number" value={genYear} onChange={e => setGenYear(e.target.value)}
                        style={{ ...inpStyle, width: 72, textAlign: "center" }} />
                    {/* <button onClick={generateBills}
                        style={{ padding: "9px 20px", borderRadius: 9, border: "none", background: C.accent, fontSize: 12, fontWeight: 700, color: "#fff", cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 2px 8px rgba(79,110,247,0.3)" }}>
                        ⚡ Generate
                    </button> */}
                    <button
                        onClick={generateBills}
                        style={{
                            padding: "9px 20px",
                            borderRadius: 9,
                            border: "none",
                            background: C.accent,
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#fff",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            boxShadow: "0 2px 8px rgba(79,110,247,0.3)",
                            display: "flex",
                            alignItems: "center",
                            gap: 6
                        }}
                    >
                        <Zap size={14} />
                        Generate
                    </button>
                </div>

                {isLoading ? (
                    <div className="w-full h-[420px] flex items-center justify-center">
                        <InnerLoader />
                    </div>
                ) : (!recurring.length || recurring.length < 1) ? (
                    <div className="w-full h-[420px] flex items-center justify-cente pb-5">
                        <DataNotFound title={"Recurring Bills"} />
                    </div>
                ) : (
                    <>
                        {/* Table header */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 110px 90px 110px 90px 70px", gap: 8, padding: "8px 12px", borderBottom: `1px solid ${C.border}` }}>
                            {["Expense", "Category", "Location", "Amount", "Due Day", "Method", ""].map((h, i) => (
                                <div key={i} style={{ fontSize: 10, fontWeight: 700, color: C.textMut, letterSpacing: 0.5, textTransform: "uppercase" }}>{h}</div>
                            ))}
                        </div>

                        {/* Rows */}
                        {recurring.map((r, i) => {
                            const category = categories.find(c => c.name === r.cat);
                            const location = locations.find(l => l.name === r.loc);
                            return (
                            <div key={r.id} style={{ display: "grid", gridTemplateColumns: "1fr 100px 110px 90px 110px 90px 70px", gap: 8, padding: "10px 12px", borderBottom: i < recurring.length - 1 ? `1px solid ${C.border}` : "none", background: r?.is_active ? "#fff" : "#f9fafb", alignItems: "center", opacity: r?.is_active ? 1 : 0.5 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: C.textPri }}>{category?.icon || "💳"} {r.expense}</div>
                                <div style={{ fontSize: 11, color: C.textSec }}>{r.cat}</div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: location?.color || C.textSec }}>{r.loc || "—"}</div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: C.textPri }}>{pkr(r.amt)}</div>
                                <div style={{ fontSize: 11, color: C.textSec }}>{r.dueDay ? `${ord(r.dueDay)} of month` : "On receipt"}</div>
                                <div style={{ fontSize: 11, color: C.textSec }}>{r.method}</div>
                                <div style={{ display: "flex", gap: 4 }}>
                                    <button onClick={() => openEditRecur(r)}
                                        style={{ padding: "3px 8px", borderRadius: 6, border: `1px solid ${C.border}`, background: "#fff", fontSize: 10, cursor: "pointer", color: C.textSec }}>Edit</button>
                                    <button onClick={() => toggleRecur(r.id)}
                                        style={{ padding: "3px 8px", borderRadius: 6, border: `1px solid ${r?.is_active ? "#fecaca" : C.border}`, background: r?.is_active ? "#fef2f2" : "#f0fdf4", fontSize: 10, cursor: "pointer", color: r?.is_active ? C.red : C.green, fontWeight: 600 }}>
                                        {r?.is_active ? "Off" : "On"}
                                    </button>
                                </div>
                            </div>
                            );
                        })}
                    </>
                )}

            </Section>

            {/* Recurring modal */}
            {recurModal && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={() => setRecurModal(null)}>
                    <div style={{ background: "#fff", borderRadius: 16, padding: "24px", width: "100%", maxWidth: 460 }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ margin: "0 0 18px", fontSize: 16, fontWeight: 800, color: C.textPri }}>
                            {recurModal === "new" ? "Add Recurring Bill" : "Edit Recurring Bill"}
                        </h3>
                        {[
                            { label: "Expense Name", key: "expense", type: "text", placeholder: "e.g. Rent — Ittehad" },
                            { label: "Amount (PKR)", key: "amt", type: "number", placeholder: "e.g. 329450" },
                            { label: "Due Day", key: "dueDay", type: "number", placeholder: "1–31, blank = on receipt" },
                        ].map(({ label, key, type, placeholder }) => (
                            <div key={key} style={{ marginBottom: 12 }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: C.textMut, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>{label}</div>
                                <input type={type} value={recurForm[key] || ""} onChange={e => setRecurForm(p => ({ ...p, [key]: e.target.value }))} placeholder={placeholder}
                                    style={{ ...inpStyle, width: "100%" }} />
                            </div>
                        ))}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
                            <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: C.textMut, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>Category</div>
                                <select value={recurForm.cat || ""} onChange={e => setRecurForm(p => ({ ...p, cat: e.target.value }))} style={{ ...selStyle, width: "100%" }}>
                                    <option value="">Select Category</option>
                                    {categories.filter(c => c.is_active).map(c => <option key={c.id} value={c.name}>{c.icon} {c.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: C.textMut, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>Location</div>
                                <select value={recurForm.loc || ""} onChange={e => setRecurForm(p => ({ ...p, loc: e.target.value }))} style={{ ...selStyle, width: "100%" }}>
                                    <option value="">All / General</option>
                                    {locations.filter(l => l.is_active).map(l => <option key={l.id} value={l.name}>{l.emoji} {l.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: C.textMut, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>Method</div>
                                <select value={recurForm.method || ""} onChange={e => setRecurForm(p => ({ ...p, method: e.target.value }))} style={{ ...selStyle, width: "100%" }}>
                                    <option value="">Select Method</option>
                                    {paymentMethods.map(m => <option key={m?.id} value={m?.id}>{m?.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                            <button
                                onClick={() => setRecurModal(null)}
                                style={{ flex: 1, padding: "9px", borderRadius: 9, border: `1px solid ${C.border}`, background: "#fff", fontSize: 13, cursor: "pointer", color: C.textSec }}
                            >
                                Cancel
                            </button>
                            {/* {recurModal !== "new" && (
                                <button onClick={() => { deleteRecur(recurModal); setRecurModal(null); }}
                                    style={{ padding: "9px 16px", borderRadius: 9, border: "1px solid #fecaca", background: "#fef2f2", fontSize: 13, cursor: "pointer", color: C.red }}>Delete</button>
                            )} */}
                            <button onClick={saveRecur}
                                style={{ flex: 1, padding: "9px", borderRadius: 9, border: "none", background: C.accent, fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer" }}
                            >
                                {recurModal === "new" ? "Add Bill" : "Save Changes"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default RecurringBill
